import { Button, Typography } from '@breetometer/components';
import { BreetIcon } from 'BreetAssets';
import { Checkbox } from 'BreetComponents';
import { formatToTime } from 'BreetHelpers';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { INotificationItem } from './DialogNotifications.helpers';

const NotificationItem = ({
	createdAt,
	message,
	subject,
	read,
	selected,
	onSelect,
	deleteItem,
	markItemAsRead,
	deleteNotificationsLoading,
	targetRef,
}: INotificationItem) => {
	const ref = useRef<HTMLParagraphElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const [messageExpanded, setMessageExpanded] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (el && el.scrollHeight > el.clientHeight) {
			setIsTruncated(true);
		}
	}, [message]);

	// Tapping the notification body marks it read (and expands it when the text is truncated). This gives
	// short messages — which have no "View full message" affordance — a way to be marked read on view.
	const handleOpen = useCallback(() => {
		if (!read) {
			markItemAsRead?.();
		}
		if (isTruncated) {
			setMessageExpanded((prev) => !prev);
		}
	}, [markItemAsRead, read, isTruncated]);

	const messageStyle = { maxHeight: messageExpanded ? 'none' : 'auto', overflow: 'hidden' };
	const messageClass = `${messageExpanded ? '' : 'truncate'} color-neutral-50`;

	const wrapperClass = `notifications_allmessagesgroup_content${read ? ' read bg-primary-500 theme-border-none border-bottom-dark-navy last-child' : ' bg-primary-400'}`;

	return (
		<div
			className={wrapperClass}
			ref={targetRef}
		>
			<BreetIcon hideText />
			<div className='notifications_allmessagesgroup_content_body'>
				{/* Enter/Space activation is handled globally for any [role="button"] via setupA11y (helpers/a11y.ts). */}
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
				<div
					className='notifications_allmessagesgroup_content_bodymain cursor_pointer'
					role='button'
					tabIndex={0}
					aria-label={`Open notification: ${subject}`}
					aria-expanded={isTruncated ? messageExpanded : undefined}
					onClick={handleOpen}
				>
					<div className='notifications_allmessagesgroup_content_bodyheader'>
						{!read && <div className='notifications_allmessagesgroup_content_unread' />}
						<Typography
							as='h5'
							className={`${messageExpanded || !isTruncated ? 'read' : ''} color-primary-100`}
							title={subject}
						>
							{subject}
						</Typography>
						<Typography
							as='h6'
							className='color-primary-100'
						>
							{formatToTime(createdAt)}
						</Typography>
					</div>
					<Typography
						as='p'
						ref={ref}
						style={messageStyle}
						className={messageClass}
					>
						{message}
					</Typography>
				</div>

				<div className='notifications_allmessagesgroup_content_bodycta'>
					{isTruncated && (
						<>
							<Button
								onClick={handleOpen}
								label={messageExpanded ? 'Show less' : 'View full message'}
							/>
							<span className='color-primary-300 txt-separator hidden'>|</span>
						</>
					)}
					<div className='notifications_allmessagesgroup_content_bodycta_delete hidden'>
						<Button
							onClick={deleteItem}
							label='Delete'
						/>
						{deleteNotificationsLoading && <div className='button-spinner' />}
					</div>
					<Checkbox
						checked={selected}
						onChange={onSelect}
						className='cursor_pointer theme-background-none'
					/>
				</div>
			</div>
		</div>
	);
};

export default NotificationItem;
